import { cn } from '@/lib/utils';
import React, { useState, useRef, useEffect } from 'react';

interface TooltipProps {
	children: React.ReactElement;
	content: React.ReactNode;
	position?: 'top' | 'bottom' | 'left' | 'right';
	delay?: number;
	className?: string;
	disabled?: boolean;
	tapToPinOnTouch?: boolean;
	interactive?: boolean;
	hideDelay?: number;
}

const Tooltip = ({
	children,
	content,
	position = 'top',
	delay = 100,
	disabled,
	tapToPinOnTouch = false,
	interactive = false,
	hideDelay = 150,
	className = '',
}: TooltipProps) => {
	const [visible, setVisible] = useState(false);
	const [coords, setCoords] = useState({ x: 0, y: 0 });
	const [arrowOffset, setArrowOffset] = useState({ x: 0, y: 0 });
	const [isTouchLikeDevice, setIsTouchLikeDevice] = useState(false);
	const [isPinnedByTap, setIsPinnedByTap] = useState(false);
	const triggerRef = useRef<HTMLElement>(null);
	const tooltipRef = useRef<HTMLDivElement>(null);
	const timeoutRef = useRef<NodeJS.Timeout>();
	const hideTimeoutRef = useRef<NodeJS.Timeout>();
	const isHoveringTooltipRef = useRef(false);

	const updatePosition = () => {
		if (triggerRef.current && tooltipRef.current) {
			const triggerRect = triggerRef.current.getBoundingClientRect();
			const tooltipRect = tooltipRef.current.getBoundingClientRect();
			const viewportWidth = window.innerWidth;
			const viewportHeight = window.innerHeight;

			let x = 0;
			let y = 0;

			const gap = interactive ? 4 : 8;

			switch (position) {
				case 'top':
					x =
						triggerRect.left +
						triggerRect.width / 2 -
						tooltipRect.width / 2;
					y = triggerRect.top - tooltipRect.height - gap;
					break;
				case 'bottom':
					x =
						triggerRect.left +
						triggerRect.width / 2 -
						tooltipRect.width / 2;
					y = triggerRect.top + triggerRect.height + gap;
					break;
				case 'left':
					x = triggerRect.left - tooltipRect.width - gap;
					y =
						triggerRect.top +
						triggerRect.height / 2 -
						tooltipRect.height / 2;
					break;
				case 'right':
					x = triggerRect.left + triggerRect.width + gap;
					y =
						triggerRect.top +
						triggerRect.height / 2 -
						tooltipRect.height / 2;
					break;
			}

			x = Math.max(8, Math.min(x, viewportWidth - tooltipRect.width - 8));
			y = Math.max(
				8,
				Math.min(y, viewportHeight - tooltipRect.height - 8),
			);

			setCoords({ x, y });

			const triggerCenterX = triggerRect.left + triggerRect.width / 2;
			const triggerCenterY = triggerRect.top + triggerRect.height / 2;
			const clampedArrowX = Math.max(
				10,
				Math.min(triggerCenterX - x, tooltipRect.width - 10),
			);
			const clampedArrowY = Math.max(
				10,
				Math.min(triggerCenterY - y, tooltipRect.height - 10),
			);
			setArrowOffset({ x: clampedArrowX, y: clampedArrowY });
		}
	};

	const showTooltip = () => {
		if (isPinnedByTap) return;
		clearTimeout(hideTimeoutRef.current);
		timeoutRef.current = setTimeout(() => {
			setVisible(true);
		}, delay);
	};

	const scheduleHide = () => {
		if (isPinnedByTap) return;
		clearTimeout(hideTimeoutRef.current);
		hideTimeoutRef.current = setTimeout(() => {
			if (!isHoveringTooltipRef.current) {
				setVisible(false);
			}
		}, hideDelay);
	};

	const hideTooltip = () => {
		if (isPinnedByTap) return;
		clearTimeout(timeoutRef.current);
		if (interactive) {
			scheduleHide();
			return;
		}
		setVisible(false);
	};

	const hideTooltipImmediately = () => {
		clearTimeout(timeoutRef.current);
		clearTimeout(hideTimeoutRef.current);
		isHoveringTooltipRef.current = false;
		setVisible(false);
		setIsPinnedByTap(false);
	};

	const handleTooltipMouseEnter = () => {
		if (!interactive) return;
		isHoveringTooltipRef.current = true;
		clearTimeout(hideTimeoutRef.current);
	};

	const handleTooltipMouseLeave = () => {
		if (!interactive) return;
		isHoveringTooltipRef.current = false;
		scheduleHide();
	};

	useEffect(() => {
		if (visible) {
			updatePosition();
			const handleScroll = () => {
				if (visible) {
					updatePosition();
				}
			};

			window.addEventListener('scroll', handleScroll, true);
			window.addEventListener('resize', handleScroll);

			return () => {
				window.removeEventListener('scroll', handleScroll, true);
				window.removeEventListener('resize', handleScroll);
			};
		}
	}, [visible]);

	useEffect(() => {
		return () => {
			clearTimeout(timeoutRef.current);
			clearTimeout(hideTimeoutRef.current);
		};
	}, []);

	useEffect(() => {
		if (typeof window === 'undefined') return;
		const mq = window.matchMedia('(hover: none), (pointer: coarse)');
		const update = () => setIsTouchLikeDevice(mq.matches);
		update();
		mq.addEventListener?.('change', update);
		return () => mq.removeEventListener?.('change', update);
	}, []);

	useEffect(() => {
		if (!visible || !isPinnedByTap) return;

		const onAnyInteraction = (event: Event) => {
			const triggerEl = triggerRef.current;
			if (
				triggerEl &&
				event.target instanceof Node &&
				triggerEl.contains(event.target)
			) {
				return;
			}
			hideTooltipImmediately();
		};

		const onScrollOrResize = () => hideTooltipImmediately();

		document.addEventListener('pointerdown', onAnyInteraction, true);
		document.addEventListener('touchstart', onAnyInteraction, true);
		document.addEventListener('keydown', onAnyInteraction, true);
		window.addEventListener('scroll', onScrollOrResize, true);
		window.addEventListener('resize', onScrollOrResize);

		return () => {
			document.removeEventListener('pointerdown', onAnyInteraction, true);
			document.removeEventListener('touchstart', onAnyInteraction, true);
			document.removeEventListener('keydown', onAnyInteraction, true);
			window.removeEventListener('scroll', onScrollOrResize, true);
			window.removeEventListener('resize', onScrollOrResize);
		};
	}, [visible, isPinnedByTap]);

	const childProps = children.props as Record<string, unknown>;
	const triggerElement = React.cloneElement(children, {
		ref: triggerRef,
		onMouseEnter: (event: React.MouseEvent<HTMLElement>) => {
			(
				childProps.onMouseEnter as
					| ((e: React.MouseEvent<HTMLElement>) => void)
					| undefined
			)?.(event);
			showTooltip();
		},
		onMouseLeave: (event: React.MouseEvent<HTMLElement>) => {
			(
				childProps.onMouseLeave as
					| ((e: React.MouseEvent<HTMLElement>) => void)
					| undefined
			)?.(event);
			hideTooltip();
		},
		onFocus: (event: React.FocusEvent<HTMLElement>) => {
			(
				childProps.onFocus as
					| ((e: React.FocusEvent<HTMLElement>) => void)
					| undefined
			)?.(event);
			showTooltip();
		},
		onBlur: (event: React.FocusEvent<HTMLElement>) => {
			(
				childProps.onBlur as
					| ((e: React.FocusEvent<HTMLElement>) => void)
					| undefined
			)?.(event);
			hideTooltip();
		},
		onClick: (event: React.MouseEvent<HTMLElement>) => {
			(
				childProps.onClick as
					| ((e: React.MouseEvent<HTMLElement>) => void)
					| undefined
			)?.(event);
			if (!tapToPinOnTouch || !isTouchLikeDevice) return;
			event.preventDefault();
			event.stopPropagation();
			clearTimeout(timeoutRef.current);
			if (visible && isPinnedByTap) {
				hideTooltipImmediately();
				return;
			}
			setVisible(true);
			setIsPinnedByTap(true);
		},
	});

	return (
		<div className="relative">
			{triggerElement}

			{visible && (
				<div
					ref={tooltipRef}
					onMouseEnter={handleTooltipMouseEnter}
					onMouseLeave={handleTooltipMouseLeave}
					className={cn(
						`fixed z-50 whitespace-nowrap rounded-md bg-zinc-800 px-3 py-1.5
            text-sm text-white shadow-lg
            transition-opacity duration-200`,
						interactive && 'pointer-events-auto select-text',
						disabled &&
							'bg-transparent text-transparent shadow-none',
						className,
					)}
					style={{
						left: `${coords.x}px`,
						top: `${coords.y}px`,
					}}
				>
					{content}
					<div
						className={cn(
							'absolute h-2 w-2 rotate-45 transform bg-zinc-800',
							position === 'top'
								? '-bottom-1 -translate-x-1/2'
								: '',
							position === 'bottom'
								? '-top-1 -translate-x-1/2'
								: '',
							position === 'left'
								? '-right-1 -translate-y-1/2'
								: '',
							position === 'right'
								? '-left-1 -translate-y-1/2'
								: '',
							disabled && 'bg-transparent',
						)}
						style={{
							left:
								position === 'top' || position === 'bottom'
									? `${arrowOffset.x}px`
									: undefined,
							top:
								position === 'left' || position === 'right'
									? `${arrowOffset.y}px`
									: undefined,
						}}
					/>
				</div>
			)}
		</div>
	);
};

export { Tooltip };
